"use client";

import { useEffect, useRef, type ReactNode } from "react";

type SplitTitleProps = {
  children: ReactNode;
  className?: string;
  as?: "h2" | "h3" | "p";
  id?: string;
};

/**
 * Section title with a masked line reveal (SplitText), scaling in from 0.96.
 * The text is always real and visible in the HTML. Lines are hidden only once
 * GSAP has loaded and the title is still below the viewport, so nothing flashes
 * and nothing stays hidden if the script never arrives.
 */
export const SplitTitle = ({
  children,
  className,
  as: Tag = "h2",
  id,
}: SplitTitleProps) => {
  const ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (typeof IntersectionObserver === "undefined") return;

    let revert: (() => void) | undefined;
    let cancelled = false;

    const near = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        near.disconnect();
        void Promise.all([import("gsap"), import("gsap/SplitText")]).then(
          ([{ gsap }, { SplitText }]) => {
            if (cancelled) return;
            // Already on screen: leave it alone rather than hide visible text.
            if (element.getBoundingClientRect().top < window.innerHeight * 0.9)
              return;
            gsap.registerPlugin(SplitText);
            const split = SplitText.create(element, {
              type: "lines",
              mask: "lines",
            });
            gsap.set(split.lines, { yPercent: 110 });
            gsap.set(element, { scale: 0.96, transformOrigin: "0% 100%" });

            const reveal = new IntersectionObserver(
              ([revealEntry]) => {
                if (!revealEntry?.isIntersecting) return;
                reveal.disconnect();
                gsap.to(split.lines, {
                  yPercent: 0,
                  duration: 0.9,
                  ease: "power4.out",
                  stagger: 0.08,
                });
                gsap.to(element, { scale: 1, duration: 1, ease: "power4.out" });
              },
              { rootMargin: "0px 0px -12% 0px" },
            );
            reveal.observe(element);
            revert = () => {
              reveal.disconnect();
              split.revert();
              gsap.set(element, { clearProps: "transform" });
            };
          },
        );
      },
      { rootMargin: "0px 0px 400px 0px" },
    );
    near.observe(element);

    return () => {
      cancelled = true;
      near.disconnect();
      revert?.();
    };
  }, []);

  return (
    <Tag ref={ref} id={id} className={className}>
      {children}
    </Tag>
  );
};
